import { forwardRef, useRef, useImperativeHandle } from "react";
import { Trash2, Upload } from "lucide-react";
import DocumentSmall from "../../../assets/svgs/campaigns/doc.svg";

interface ImageUploadProps {
  label?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  required?: boolean;
  fullWidth?: boolean;
  maxSize?: number; // in bytes
  accept?: string;
  description?: string;
}

const ImageUpload = forwardRef<HTMLInputElement, ImageUploadProps>(
  (
    {
      label,
      value,
      onChange,
      error,
      fullWidth = false,
      maxSize = 5 * 1024 * 1024, // 5MB default
      accept = ["image/jpeg", "image/jpg", "image/png"],
      description,
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Expose the input ref to parent components
    useImperativeHandle(ref, () => fileInputRef.current as HTMLInputElement);

    const handleFileChange = (file: File | null) => {
      if (!file) {
        onChange(null);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        alert(
          `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`
        );
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      onChange(file);
    };

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleRemove = () => {
      onChange(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    return (
      <div
        className={`flex relative flex-col gap-2 ${fullWidth ? "w-full" : ""}`}
      >
        {label && (
          <label className="font-cabinetgrotesk md:font-medium text-sm md:text-lg leading-none tracking-[-0.36px]">
            {label}
          </label>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={Array.isArray(accept) ? accept.join(",") : accept}
          onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
          className="hidden"
        />

        {value ? (
          // File uploaded state
          <div className="relative flex justify-between items-center gap-4 py-9 pr-4 pl-4 border border-black rounded-xl h-16">
            <div className="flex items-center gap-2">
              <div className="flex justify-center items-center bg-gray-100 rounded-lg w-8 h-8 text-black">
                <DocumentSmall />
              </div>
              <p className="w-32 md:w-64 font-semibold text-nowrap text-sm truncate overflow-hidden">
                {value.name}
              </p>
            </div>
            <button type="button" onClick={handleRemove}>
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
          </div>
        ) : (
          // Empty state - upload button
          <div
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleClick();
              }
            }}
            role="button"
            tabIndex={0}
            className="flex items-center gap-2 hover:bg-gray-50 py-9 pl-6 border border-black rounded-xl h-16 font-semibold text-gray-600 transition-colors cursor-pointer"
          >
            <Upload className="w-5 h-5" />
            <p className="text-sm leading-5">Select image</p>
          </div>
        )}

        {description && !value && (
          <p className="text-gray-500 text-xs">{description}</p>
        )}

        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>
    );
  }
);

ImageUpload.displayName = "ImageUpload";

export default ImageUpload;
